// src/features/roles/types.ts

/* =========================
 * ROLES — Types & Contracts
 * ========================= */

/**
 * Wire (transport) DTOs as the backend exposes them.
 *
 * IMPORTANT:
 * - Frontend does NOT deal with status flags (Active/In-Active) anymore.
 * - Frontend does NOT care about audit fields (createdBy/At, updatedBy/At, deletedBy/At).
 * - Backend is responsible for only returning "active" / non-deleted roles.
 *
 * We keep only the fields that matter to the UI: id, name, code, description.
 */
export namespace RoleDTO {
  /** Base role object as transported over the wire */
  export interface Base {
    id: number;
    name: string;                 // e.g., "Admin"
    code?: string | null;         // optional stable key/slug, e.g., "ADMIN"
    description?: string | null;  // human description
    isSystemRole?: boolean;       // assignable from the admin user forms
    // NOTE: Backend may still send status/audit fields, but
    // we intentionally ignore them on the frontend.
  }

  /** Create payload accepted by the API */
  export interface Create {
    name: string;
    code?: string | null;
    description?: string | null;
    isSystemRole?: boolean;
    // no status field – backend decides defaults & activity
  }

  /** Update payload accepted by the API */
  export interface Update {
    name?: string;
    code?: string | null;
    description?: string | null;
    isSystemRole?: boolean;
    // no status field – backend handles any status logic internally
  }

  /** List/query params for pagination & filters */
  export interface ListParams {
    page?: number;      // 1-based
    pageSize?: number;  // default 20
    q?: string;         // search across name/code
    // no status filter – backend only returns active roles
  }

  /** Generic paginated page */
  export interface Page<T> {
    items: T[];
    total: number;
    page: number;
    pageSize: number;
  }

  /* ---------- Permissions / Grants ---------- */

  export interface Permission {
    id: number;
    module: string;          // e.g., "users"
    action: string;          // e.g., "read" | "create" | "update" | "delete"
    label?: string;          // optional human label
  }

  export interface RolePermission {
    roleId: number;
    permissionId: number;
  }

  /** Grants response for a given role */
  export interface RoleGrantsResponse {
    role: Base;
    permissions: Permission[];  // full catalog (if server returns)
    granted: number[];          // permissionIds assigned to the role
  }

  export interface UpdateGrantsPayload {
    permissionIds: number[];    // overwrite set
  }
}

/** =========================
 * UI model (view layer)
 * =========================
 * NOTE:
 * - No status field.
 * - No audit timestamps.
 * - This is exactly what UI cares about.
 */
export interface Role {
  id: number;
  name: string;
  code?: string | null;
  description?: string | null;
  /** Internal "system user" role; only these are offered in user role dropdowns. */
  isSystemRole: boolean;
}

/** =========================
 * React Query Keys
 * ========================= */
export const RolesKeys = {
  all: ["roles"] as const,
  list: (p: Partial<RoleDTO.ListParams>) => ["roles", "list", p] as const,
  detail: (id: number) => ["roles", "detail", id] as const,
  grants: (id: number) => ["roles", "grants", id] as const,
};
